import { FC, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Stack,
  Button,
  Typography,
  Switch,
  toast,
  ToggleGroup,
  ToggleGroupItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popconfirm,
  Spinner,
} from "@altimateai/lego";
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconCalendar,
  IconMail,
  IconHistory,
  IconSend,
} from "@tabler/icons-react";
import {
  getPlaygroundSchedules,
  deletePlaygroundSchedule,
  togglePlaygroundSchedule,
  sendNowPlaygroundSchedule,
} from "@/helpers/apis";
import { PlaygroundSchedule } from "./types";
import RunHistory from "./RunHistory";
import { formatCronFrequency } from "./scheduleDisplay";

interface SchedulesListProps {
  sessionId?: string;
  onEdit?: (schedule: PlaygroundSchedule) => void;
}

const SchedulesList: FC<SchedulesListProps> = ({ sessionId, onEdit }) => {
  const queryClient = useQueryClient();
  const [expandedHistory, setExpandedHistory] = useState<string | null>(null);
  const [showAllSessions, setShowAllSessions] = useState(!sessionId);

  // Fetch schedules — filtered by session or all
  const { data: schedulesData, isLoading } = useQuery({
    queryKey: ["playgroundSchedules", showAllSessions ? "all" : sessionId],
    queryFn: () => getPlaygroundSchedules(showAllSessions ? {} : { session_id: sessionId }),
  });

  // Toggle schedule mutation
  const { mutate: toggleSchedule } = useMutation({
    mutationFn: (scheduleId: string) => togglePlaygroundSchedule(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playgroundSchedules"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to toggle schedule",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Send now mutation
  const { mutate: sendNow, isPending: isSending } = useMutation({
    mutationFn: (scheduleId: string) => sendNowPlaygroundSchedule(scheduleId),
    onSuccess: () => {
      toast({ title: "Report is being generated and will be emailed shortly" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send report",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete schedule mutation
  const { mutate: removeSchedule, isPending: isDeleting } = useMutation({
    mutationFn: (scheduleId: string) => deletePlaygroundSchedule(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playgroundSchedules"] });
      toast({ title: "Schedule deleted" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete schedule",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleEdit = (schedule: PlaygroundSchedule) => {
    onEdit?.(schedule);
  };

  // Cron → human-readable cadence. Shared with the dialog's success view
  // via ./scheduleDisplay so list and dialog stay phrased identically.
  const formatFrequency = formatCronFrequency;

  const formatNextRun = (nextRun: string | null): string => {
    if (!nextRun) return "Not scheduled";
    const date = new Date(nextRun);
    return date.toLocaleString();
  };

  const schedules = schedulesData?.schedules || [];

  if (isLoading) {
    return (
      <Stack direction="row" align="center" justify="center" className="al-py-8">
        <Spinner size="md" />
      </Stack>
    );
  }

  return (
    <div className="al-space-y-4">
      <Stack direction="row" align="center" justify="between">
        <Typography variant="h4">Scheduled Reports</Typography>
        {sessionId && (
          <ToggleGroup
            type="single"
            value={showAllSessions ? "all" : "session"}
            onValueChange={val => val && setShowAllSessions(val === "all")}
            className="al-border al-rounded-md al-p-0.5"
          >
            <ToggleGroupItem value="session" className="al-text-xs al-px-3 al-py-1 al-h-auto">
              This session
            </ToggleGroupItem>
            <ToggleGroupItem value="all" className="al-text-xs al-px-3 al-py-1 al-h-auto">
              All sessions
            </ToggleGroupItem>
          </ToggleGroup>
        )}
      </Stack>

      {schedules.length === 0 ? (
        <Stack
          direction="column"
          align="center"
          justify="center"
          className="al-py-12 al-text-center al-border al-rounded-lg al-bg-muted/30"
        >
          <IconCalendar className="al-h-12 al-w-12 al-text-muted-foreground al-mb-4" />
          <Typography variant="body" className="al-text-muted-foreground">
            No scheduled reports yet.
          </Typography>
        </Stack>
      ) : (
        <div className="al-space-y-3">
          {schedules.map((schedule: PlaygroundSchedule) => (
            <div
              key={schedule.id}
              className="al-border al-rounded-lg al-p-4 al-bg-white al-shadow-sm"
            >
              <Stack direction="column">
                <Stack direction="row" align="start" justify="between">
                  <div className="al-flex-1">
                    <Stack direction="row" align="center" gap="sm" className="al-mb-2">
                      <Typography variant="h4">{schedule.name}</Typography>
                      <span
                        className={`al-px-2 al-py-0.5 al-text-xs al-rounded-full ${
                          schedule.enabled
                            ? "al-bg-green-100 al-text-green-700"
                            : "al-bg-gray-100 al-text-gray-600"
                        }`}
                      >
                        {schedule.enabled ? "Active" : "Paused"}
                      </span>
                    </Stack>

                    <Stack
                      direction="column"
                      gap="xs"
                      className="al-text-sm al-text-muted-foreground"
                    >
                      <Stack direction="row" align="center" gap="xs">
                        <IconCalendar className="al-h-4 al-w-4" />
                        <span>{formatFrequency(schedule.cron_expr)}</span>
                      </Stack>
                      <Stack direction="row" align="center" gap="xs">
                        <IconMail className="al-h-4 al-w-4" />
                        <span>{schedule.emails.join(", ")}</span>
                      </Stack>
                      <span className="al-text-xs">
                        Next run: {formatNextRun(schedule.next_run)}
                      </span>
                    </Stack>
                  </div>

                  <Stack direction="row" align="center" gap="sm">
                    <Switch
                      checked={schedule.enabled}
                      onCheckedChange={() => toggleSchedule(schedule.id)}
                    />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" title="More options">
                          <IconDotsVertical className="al-h-4 al-w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            setExpandedHistory(prev => (prev === schedule.id ? null : schedule.id))
                          }
                        >
                          <IconHistory className="al-h-4 al-w-4 al-mr-2" />
                          {expandedHistory === schedule.id ? "Hide History" : "View History"}
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled={isSending} onClick={() => sendNow(schedule.id)}>
                          <IconSend className="al-h-4 al-w-4 al-mr-2" />
                          Send Now
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEdit(schedule)}>
                          <IconEdit className="al-h-4 al-w-4 al-mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <Popconfirm
                          title="Delete schedule"
                          description="Are you sure you want to delete this schedule?"
                          onConfirm={() => removeSchedule(schedule.id)}
                        >
                          <DropdownMenuItem
                            disabled={isDeleting}
                            className="al-text-red-600 focus:al-text-red-600"
                            onSelect={(e: Event) => e.preventDefault()}
                          >
                            <IconTrash className="al-h-4 al-w-4 al-mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </Popconfirm>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Stack>
                </Stack>

                {expandedHistory === schedule.id && (
                  <div className="al-mt-3 al-pt-3 al-border-t">
                    <Stack direction="row" align="center" gap="xs" className="al-mb-2">
                      <IconHistory className="al-h-4 al-w-4 al-text-muted-foreground" />
                      <Typography className="al-text-sm al-font-medium">Run History</Typography>
                    </Stack>
                    <RunHistory scheduleId={schedule.id} />
                  </div>
                )}
              </Stack>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SchedulesList;
